import { useCallback, useState, useEffect } from 'react';
import { endpoints } from '@/lib/axios/axios-global';
import { apiGet } from '@repo/lib/axios/axios-utils';
import { addPatient } from '../../types/patient.types';
import { Table } from '@tanstack/react-table';
import { DataTable } from '../reportsList/DataTable';
import { usePatientColumns } from '../reportsList/columns';
import { DataTableToolbar } from './data-table-toolbar';
import { FetchDataOptions } from '@/types/table.types';
import { toast } from 'sonner';

function mapApiToPatient(raw: Record<string, unknown>): addPatient {
    const patient = raw.patient as Record<string, unknown> | undefined;
    const phone =
        String(patient?.phone ?? raw.patient_phone ?? raw.phone ?? '').trim();
    const address =
        String(patient?.address ?? raw.patient_address ?? raw.address ?? '').trim();
    const patientId = raw.patient_id != null
        ? String(raw.patient_id)
        : (patient?.id != null ? String(patient.id) : '');

    return {
        id: Number(raw.id ?? 0),
   
        phone,
        address: address || '',
        email: String(raw.email ?? patient?.email ?? '').trim(),
        orders_count: Number(raw.orders_count ?? patient?.orders_count ?? 0),
        is_black_list: Number(raw.is_black_list ?? patient?.is_black_list ?? 0),
        patient_id: patientId,
        status: String(raw.status ?? raw.booking_status ?? raw.bookingStatus ?? '').trim(),
        created_at: String(raw.created_at ?? '').trim(),
        updated_at: String(raw.updated_at ?? '').trim(),
        patient_name: patient?.name ?? '',
        patient_phone: patient?.phone ?? '',
        patient_address: patient?.address ?? '',
        service_type: String(raw.service_type ?? '').trim(),
    } as unknown as addPatient;
}

const ReportsPatientList = () => {
    const columns = usePatientColumns();
    const [data, setData] = useState<addPatient[]>([]);
    const [pageCount, setPageCount] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [filterStatus, setFilterStatus] = useState<string>('');
    const [globalFilter, setGlobalFilter] = useState<string>('');
    const [tableInstance, setTableInstance] = useState<Table<addPatient> | null>(null);

    const fetchData = useCallback(async (options: FetchDataOptions) => {
        setIsLoading(true);
        try {
            const pageSize = options.pageSize || 10;
            const queryParams: Record<string, string | number> = {
                page: options.pageIndex + 1,
                per_page: pageSize,
                search: (options.globalFilter || '').trim(),
            };
            if (options.filters?.status) {
                queryParams.status = String(options.filters.status);
            }

            const response = await apiGet<{
                data?: { data?: unknown[]; pagination?: { total?: number } };
            }>({
                endpoint: endpoints.bookings.list,
                queryParams,
            });

            const payload = response?.data as Record<string, unknown> | undefined;
            const inner = payload?.data as
                | { data?: unknown[]; pagination?: { total?: number } }
                | undefined;
            const rawList =
                inner?.data ?? (Array.isArray(payload?.data) ? payload.data : []);
            const list = Array.isArray(rawList)
                ? rawList.map((row) => mapApiToPatient(row as Record<string, unknown>))
                : [];

            const pagination =
                inner?.pagination ?? (payload?.pagination as { total?: number });
            const total = Number(pagination?.total ?? 0);

            setData(list);
            setPageCount(Math.ceil(total / pageSize) || 1);
            setTotalCount(total);
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('فشل تحميل بيانات المرضى');
            setData([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (tableInstance) {
            fetchData({
                pageIndex: tableInstance.getState().pagination.pageIndex,
                pageSize: 10,
                sorting: [],
                filters: { status: filterStatus },
                filterStatue: { status: filterStatus },
                globalFilter,
            });
        }
    }, [globalFilter, filterStatus, fetchData, tableInstance]);

    const handleApplyFilter = useCallback(() => {
        // Filter applied via setFilterStatus in toolbar; useEffect refetches
    }, []);

    return (
        <div className="mb-10 mt-4 rounded-lg border border-[#E8E7EA] shadow-[0px_4px_4px_-1px_#0C0D0D0D,0px_4px_4px_-1px_#0C0D0D1A] z-20">
            <DataTable
                columns={columns}
                data={data}
                pageCount={pageCount}
                totalCount={totalCount}
                onFetchData={(options) => fetchData(options)}
                isLoading={isLoading}
                DataTableToolbar={(tableProps) => (
                    <DataTableToolbar
                        table={tableProps.table}
                        globalFilter={globalFilter}
                        setGlobalFilter={setGlobalFilter}
                        filters={{ status: filterStatus }}
                        setFilters={(filters) => setFilterStatus(filters.status as string)}
                        setFilterStatus={setFilterStatus}
                        onApplyFilter={handleApplyFilter}
                        data={data}
                        handleRefreshTable={tableProps.handleRefreshTable}
                    />
                )}
                onTableInstance={(table) => setTableInstance(table)}
            />
        </div>
    );
};

export default ReportsPatientList;
